import React, { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { gantt } from 'dhtmlx-gantt/dhtmlxgantt';
// mui components
import { LoadingButton } from '@mui/lab';
import { Dialog, Grid, Typography } from '@mui/material';
// components
import StyledForm from 'components/styled-form';
// hooks
import useToast from 'hooks/useToast';
// api
import { useIncompleteSchedulePhaseItemMutation } from 'redux-store/api/schedule-phases-items';
import { IIncompleteItemForm } from './types';
// styles
import { styles } from './styles';
import { IPhaseItem, PhaseStatusesEnum } from 'redux-store/types';
import { TEMP_ITEM_ID_COEFFICIENT } from 'utils/constants';

interface OwnProps {
  open: boolean;
  item: IPhaseItem;
  projectId: number;
  updateGanttChart: boolean;
  closeModal?: () => void;
  closePhaseItemModal?: () => void;
  handleAllowUpdateProject?: () => void;
}

const IncompleteItemModal: FC<OwnProps> = ({
  open,
  item,
  projectId,
  updateGanttChart,
  closeModal,
  closePhaseItemModal,
  handleAllowUpdateProject,
}) => {
  const [incompletePhaseItem] = useIncompleteSchedulePhaseItemMutation();
  const showToast = useToast();

  const errorMessage = (err) => {
    if (typeof err?.data?.message === 'string') {
      return err?.data?.message;
    }

    return 'Something went wrong. Please try again or contact support for help.';
  };

  const form = useForm<IIncompleteItemForm>({
    defaultValues: {
      endDate: new Date(),
    },
  });

  const onSubmit = async () => {
    incompletePhaseItem({
      id: item?.id,
      phaseId: item?.phaseId,
      projectId: projectId,
    })
      .unwrap()
      .then(() => {
        if (!!updateGanttChart) {
          const updatingTask = gantt.getTask(item.id * TEMP_ITEM_ID_COEFFICIENT);
          gantt.updateTask(String(item.id * TEMP_ITEM_ID_COEFFICIENT), {
            ...updatingTask,
            item_status: PhaseStatusesEnum.IN_PROGRESS,
          });
        }

        closeModal();
        closePhaseItemModal?.();
      })
      .catch((err) => {
        showToast({
          type: 'error',
          message: errorMessage(err),
        });
      })
      .finally(() => {
        if (!!handleAllowUpdateProject) {
          handleAllowUpdateProject();
        }
      });
  };

  return (
    <Dialog open={open} sx={styles.modal} onClose={closeModal}>
      <FormProvider {...form}>
        <StyledForm onSubmit={form.handleSubmit(onSubmit)}>
          <Grid sx={styles.titleContainer}>
            <Typography sx={styles.title}>Incomplete Item</Typography>

            <Typography sx={styles.subtitle}>
              Are you sure you would like to mark this schedule item incomplete?
            </Typography>
          </Grid>

          <Grid sx={styles.buttonsContainer}>
            <LoadingButton variant="outlined" onClick={closeModal} sx={styles.cancelButton}>
              Cancel
            </LoadingButton>
            <LoadingButton type="submit" variant="contained" sx={styles.saveButton}>
              Confirm
            </LoadingButton>
          </Grid>
        </StyledForm>
      </FormProvider>
    </Dialog>
  );
};

export default IncompleteItemModal;
